import { Component, OnInit, ViewEncapsulation, Inject, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, catchError, switchMap, tap, map, Observable } from 'rxjs';

import { AccountService } from 'app/core';
import { ReorderService } from '@services/reorder.service';
import { ObjectGroup } from 'app/shared/models/object-group';
import { PipeLineAddDialogData } from './pipeline-add.types';
import { Pipeline } from 'app/modules/projects/projects.types';
import { ObjectGroupService } from 'app/services/object-group.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import * as CustomValidator from 'app/shared/directives/custom-validators.directive';
import { Team, User } from '@core/models';
import { TeamsService } from 'app/modules/settings/teams/services/teams.service';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';

@Component({
  selector: 'pipeline-add',
  templateUrl: './pipeline-add.component.html',
  styleUrls: ['./pipeline-add.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PipelineAddComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  addViewForm: UntypedFormGroup;
  editViewForm: UntypedFormGroup;
  addOrEditGroupForm: UntypedFormGroup;
  pipelines: Pipeline[];
  viewGroups: ObjectGroup[];
  viewGroupOptions: ObjectGroup[];
  viewGroupReorderOptions: ObjectGroup[];
  isLoading: boolean;
  users: User[];
  teams: Team[];
  currentGroupId: number;
  currentUser: User;

  teams$ = this.teamService.getTeams(1, 1000).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(({ results }) => {
      return TeamsService.flattenTeamsAndAddMetadata(results);
    }),
  );

  activeUsers$: Observable<User[]> = this.store.select(UserState.getActiveUsers);
  currentUser$: Observable<User> = this.store.select(UserState.getUser);

  private get accountId(): number {
    return Number(this.accountService.getCurrentAccount()?.id);
  }

  get isSaveBtnDisabled(): boolean {
    if (this.dialogData.creatingView) return this.isLoading || this.addViewForm.pristine || this.addViewForm.invalid;

    if (this.dialogData.editingView) return this.isLoading || this.editViewForm.pristine || this.editViewForm.invalid;

    if (this.dialogData.creatingGroup || this.dialogData.editingGroup)
      return this.isLoading || this.addOrEditGroupForm.pristine || this.addOrEditGroupForm.invalid;
  }

  get dynamicDialogTitle(): string {
    if (this.dialogData.creatingView) return 'Add View';
    if (this.dialogData.editingView) return 'Edit View';
    if (this.dialogData.creatingGroup) return 'Add Group';
    if (this.dialogData.editingGroup) return 'Edit Group';
    if (this.dialogData.reorderingGroups) return 'Reorder Groups';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: PipeLineAddDialogData,
    private readonly matDialogRef: MatDialogRef<PipelineAddComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly projectsService: ProjectsService,
    private readonly accountService: AccountService,
    private readonly objectGroupService: ObjectGroupService,
    private readonly reorderService: ReorderService,
    private readonly teamService: TeamsService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.activeUsers$.subscribe((users) => {
      this.users = users;
    });

    this.currentUser$.subscribe((user) => {
      this.currentUser = user;
    });

    this.getObjectGroups();
    this.pipelines = this.dialogData?.pipelines;
    this.currentGroupId = +this.dialogData?.selectedViewGroup?.id;
    this.buildForms();
  }

  private buildForms(): void {
    if (this.dialogData.creatingView) {
      this.addViewForm = this.formBuilder.group({
        name: [
          '',
          [
            Validators.required,
            CustomValidator.noWhiteValidator,
            CustomValidator.uniqueNameValidator(this.dialogData?.pipelines, 'name'),
          ],
        ],
        pipeline_type: [
          this.dialogData.copy_from_pipeline ? this.dialogData.copy_from_pipeline.pipeline_type : 'static',
        ],
        copy_from_pipeline_id: [this.dialogData.copy_from_pipeline ? this.dialogData.copy_from_pipeline.id : ''],
        visibility: ['public_visibility'],
        record_owner_id: [this.currentUser?.id || '', Validators.required],
        team_id: [this.currentUser?.primary_team_id || '', Validators.required],
      });
    }
    if (this.dialogData.editingView) {
      this.editViewForm = this.formBuilder.group({
        name: [this.dialogData?.pipeline?.name],
        group: [this.dialogData?.selectedViewGroup.id],
        record_owner_id: [this.dialogData?.pipeline?.record_owner_id, Validators.required],
        team_id: [this.dialogData?.pipeline?.team_id, Validators.required],
      });
    }

    if (this.dialogData.creatingGroup || this.dialogData.editingGroup) {
      const name = this.dialogData?.editingGroup ? this.dialogData?.selectedViewGroup?.label : '';

      this.addOrEditGroupForm = this.formBuilder.group({
        name: [name, [Validators.required, CustomValidator.noWhiteValidator]],
        record_owner_id: [
          +this.dialogData?.selectedViewGroup?.record_owner_id || this.currentUser?.id || '',
          Validators.required,
        ],
        team_id: [
          +this.dialogData?.selectedViewGroup?.team_id || this.currentUser?.primary_team_id || '',
          Validators.required,
        ],
      });
    }
  }

  private getObjectGroups(): void {
    if (!this.dialogData.creatingView)
      this.objectGroupService
        .get(this.accountId, 'Pipeline')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((objectGroups: ObjectGroup[]) => {
          this.viewGroups = objectGroups.sort((a, b) => a.rank - b.rank);
          this.viewGroupReorderOptions = [...this.viewGroups];
          this.viewGroups.push({ id: null, label: 'No Group' });
          this.viewGroupOptions = this.viewGroups;
        });
  }

  public discard(data?: { refresh?: boolean; newGroup?: ObjectGroup }): void {
    this.matDialogRef.close(data);
  }

  public save(): void {
    if (this.dialogData.creatingView) {
      const vals = this.addViewForm.value;

      const newPipeline = {
        name: vals.name,
        pipeline_type: vals.pipeline_type,
        visibility: vals.visibility,
        record_owner_id: vals.record_owner_id,
        team_id: vals.team_id,
      };

      this.projectsService
        .createPipeline(newPipeline)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => this.accountService.bustSavedFiltersCacheBuster()),
          switchMap((pipeline) => {
            this.accountService.getSavedFilters().subscribe();
            if (vals.copy_from_pipeline_id) {
              const template = this.pipelines.find((pipeline) => pipeline.id === vals.copy_from_pipeline_id);
              pipeline.table_definition = JSON.stringify(template.table_definition);

              return this.projectsService.updatePipeline(pipeline);
            }

            return of(pipeline);
          }),
          switchMap((pipeline: Pipeline) => {
            if (!this.dialogData.selectedViewGroup?.id) return of(pipeline);

            const newGroupViewMember = {
              groupable_type: 'Pipeline',
              groupable_id: pipeline.id,
            };

            return this.objectGroupService.createMember(
              this.accountId,
              this.dialogData.selectedViewGroup.id,
              newGroupViewMember,
            );
          }),
        )
        .subscribe(() => {
          this.accountService.bustStandardDropdownsCache();
          this.discard({ refresh: true });
        });
    }

    if (this.dialogData.editingView) {
      const vals = { ...this.editViewForm.value };

      const editGroupView = {
        groupable_type: 'Pipeline',
        groupable_id: this.dialogData?.pipeline?.id,
      };
      let newPipelineDate = { ...this.dialogData.pipeline, ...vals };
      delete newPipelineDate['group'];
      this.projectsService
        .updatePipeline(newPipelineDate)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => {
            if (vals.group !== this.currentGroupId) {
              const oldObjectGroupId = this.dialogData?.selectedViewGroup?.id;
              const oldMember = this.dialogData?.selectedViewGroup?.members?.find(
                (member) => member.groupable_id === this.dialogData?.pipeline?.id,
              );

              if (!vals.group) {
                return this.objectGroupService
                  .deleteMember(this.accountId, oldObjectGroupId, oldMember.id)
                  .pipe(takeUntilDestroyed(this.destroyRef));
              } else if (vals.group && !oldObjectGroupId) {
                return this.objectGroupService
                  .createMember(this.accountId, vals.group?.id ?? vals.group, editGroupView)
                  .pipe(takeUntilDestroyed(this.destroyRef));
              } else {
                return this.objectGroupService.deleteMember(this.accountId, oldObjectGroupId, oldMember.id).pipe(
                  takeUntilDestroyed(this.destroyRef),
                  switchMap(() => this.objectGroupService.createMember(this.accountId, vals.group, editGroupView)),
                );
              }
            }
            return of(true);
          }),
        )
        .subscribe(() => this.discard({ refresh: true }));
    }

    if (this.dialogData.creatingGroup) {
      const vals = this.addOrEditGroupForm.value;

      const newGroup = {
        label: vals.name,
        groupable_type: 'Pipeline',
        record_owner_id: vals.record_owner_id,
        team_id: vals.team_id,
      };

      this.objectGroupService
        .create(this.accountId, newGroup)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((err) => {
            if (err.error.error.includes('has already been taken')) {
              this.addOrEditGroupForm.get('name').setErrors({ duplicatedName: true });
              this.isLoading = false;
            }
            return of(null);
          }),
        )
        .subscribe((newGroupResult) => this.discard({ refresh: true, newGroup: newGroupResult }));
    }

    if (this.dialogData.editingGroup) {
      const vals = this.addOrEditGroupForm.value;

      const updatedGroup = { ...this.dialogData.selectedViewGroup, label: vals.name, ...vals };

      this.objectGroupService
        .update(this.accountId, updatedGroup)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((err) => {
            if (err.error.error.includes('has already been taken')) {
              this.addOrEditGroupForm.get('name').setErrors({ duplicatedName: true });
              this.isLoading = false;
            }
            return of(null);
          }),
        )
        .subscribe((editedGroup) => {
          if (editedGroup) this.discard({ refresh: true });
        });
    }

    if (this.dialogData.reorderingGroups) {
      const ranksPayload = this.viewGroupReorderOptions.reduce((acc, group, index) => {
        acc[group.id] = index + 1;
        return acc;
      }, {});

      this.reorderService
        .update(this.accountId, 'ObjectGroup', ranksPayload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.discard({ refresh: true }));
    }
  }

  // Function attached to debounce-click directive to flag the button's disable state.
  public isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  dropped(event: CdkDragDrop<ObjectGroup[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.viewGroupReorderOptions, event.previousIndex, event.currentIndex);
  }
}
